import { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  Building2,
  CalendarCheck2,
  Check,
  ChevronRight,
  CircleCheckBig,
  Clock3,
  Facebook,
  Instagram,
  LayoutDashboard,
  Loader2,
  MessageCircleMore,
  MousePointerClick,
  Play,
  ShieldCheck,
  Sparkles,
  Target,
  UsersRound,
  Zap,
} from 'lucide-react';
import CavernicolAppLogo from '../components/brand/CavernicolAppLogo';
import TurnstileWidget from '../components/auth/TurnstileWidget';
import api from '../lib/api';
import { CAPABILITY_PILLS, VERTICALS, DEFAULT_SHARED_CONTENT } from './landingVerticals';
import './LandingPage.css';

const EASE = [0.22, 1, 0.36, 1];

const NEUTRAL_VERTICAL = {
  id: 'all-businesses',
  label: 'Cualquier industria',
  shortLabel: 'Operación',
  icon: Bot,
  theme: {
    accent: '#F72585',
    accent2: '#F05A35',
    ink: '#131722',
    surface: '#FFF9F7',
    glow: '#FCE7F3',
  },
  eyebrow: 'Una inteligencia operativa para todo tu negocio',
  problem: 'Tus clientes escriben, llaman, preguntan y reservan por canales distintos. Tu equipo necesita ver una sola historia.',
  agitation: 'Cuando mensajería, marketing, ventas, agenda y llamadas trabajan por separado, el contexto se pierde y cada seguimiento depende de recordar qué pasó antes.',
  solution: 'CavernicolApp sincroniza conversaciones, leads, vendedores, campañas, llamadas y citas alrededor de una inteligencia central que organiza la siguiente mejor acción.',
  job: 'Convertir señales dispersas en una operación coordinada que sabe qué sigue y quién debe actuar.',
  outcome: 'Más contexto para tu equipo. Menos oportunidades perdidas.',
  alert: 'Nueva oportunidad coordinada',
  resource: 'IA + equipo comercial',
  lead: 'Un lead llegó desde mensajería',
  nextAction: 'Organizar y asignar siguiente acción',
  benefits: [
    ['Una sola memoria', 'Mensajes, llamadas, campañas, citas y seguimiento comparten el mismo contexto operativo.'],
    ['Coordinación inteligente', 'La IA conecta cada señal con la persona, automatización o acción que corresponde.'],
    ['Adaptación real', 'Elige tu industria para ver procesos, lenguaje y prioridades que tu equipo reconoce.'],
  ],
  proofPrompt: 'negocios que venden por múltiples canales',
  proofMetrics: ['Conversaciones con contexto', 'Oportunidades coordinadas', 'Seguimientos completados'],
};

function MorphContent({ active, children, className = '', ...props }) {
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={active.id}
        className={className}
        {...props}
        initial={reduceMotion ? false : { opacity: 0, y: 14, filter: 'blur(6px)' }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
        exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -10, filter: 'blur(4px)' }}
        transition={{ duration: reduceMotion ? 0.01 : 0.42, ease: EASE }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function IndustrySwitcher({ activeId, onChange }) {
  const handleKeyDown = (event, currentIndex) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    const nextIndex = (currentIndex + direction + VERTICALS.length) % VERTICALS.length;
    onChange(VERTICALS[nextIndex].id);
    document.getElementById(`vertical-${VERTICALS[nextIndex].id}`)?.focus();
  };

  return (
    <div className="industry-switcher" role="tablist" aria-label="Selecciona la industria de tu negocio">
      {VERTICALS.map((vertical, index) => {
        const Icon = vertical.icon;
        const selected = vertical.id === activeId;
        return (
          <button
            id={`vertical-${vertical.id}`}
            key={vertical.id}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls="vertical-story"
            tabIndex={selected || (!activeId && index === 0) ? 0 : -1}
            className={`industry-pill ${selected ? 'is-active' : ''}`}
            onClick={() => onChange(vertical.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {selected && (
              <motion.span
                layoutId="industry-active-pill"
                className="industry-pill__active"
                transition={{ type: 'spring', stiffness: 430, damping: 36 }}
              />
            )}
            <Icon aria-hidden="true" />
            <span>{vertical.shortLabel}</span>
          </button>
        );
      })}
    </div>
  );
}

function CyberNeuronLoop() {
  const reduceMotion = useReducedMotion();
  const nodes = [
    { id: 'messages', label: 'Mensajería', x: 92, y: 92, side: 'left' },
    { id: 'leads', label: 'Leads', x: 72, y: 210, side: 'left' },
    { id: 'sales', label: 'Vendedores', x: 92, y: 340, side: 'left' },
    { id: 'marketing', label: 'Marketing', x: 132, y: 470, side: 'left' },
    { id: 'ads', label: 'Ads', x: 668, y: 88, side: 'right' },
    { id: 'social', label: 'Social marketing', x: 688, y: 205, side: 'right' },
    { id: 'calls', label: 'Llamadas + IA', x: 678, y: 330, side: 'right' },
    { id: 'confirmations', label: 'Confirmaciones', x: 628, y: 462, side: 'right' },
    { id: 'appointments', label: 'Citas', x: 500, y: 522, side: 'right' },
    { id: 'reminders', label: 'Recordatorios', x: 270, y: 522, side: 'left' },
  ];
  const paths = [
    'M 112 92 C 220 92, 252 194, 350 250',
    'M 92 210 C 208 210, 250 244, 348 270',
    'M 112 340 C 220 340, 262 320, 350 292',
    'M 152 470 C 244 438, 286 360, 362 310',
    'M 648 88 C 548 104, 510 196, 414 250',
    'M 668 205 C 562 210, 516 244, 416 270',
    'M 658 330 C 560 330, 516 314, 416 292',
    'M 608 462 C 520 422, 482 356, 404 310',
    'M 500 502 C 476 416, 438 358, 398 318',
    'M 270 502 C 292 416, 330 358, 370 318',
  ];

  return (
    <figure className={`neural-loop ${reduceMotion ? 'is-static' : ''}`}>
      <svg viewBox="0 0 760 570" role="img" aria-labelledby="neural-loop-title neural-loop-desc">
        <title id="neural-loop-title">Inteligencia central de CavernicolApp</title>
        <desc id="neural-loop-desc">Una neurona cibernética conecta mensajería, leads, vendedores, marketing, anuncios, redes sociales, llamadas, citas y recordatorios.</desc>
        <defs>
          <radialGradient id="neural-core-gradient">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="34%" stopColor="#f72585" />
            <stop offset="100%" stopColor="#f05a35" />
          </radialGradient>
          <linearGradient id="neural-line-gradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f72585" stopOpacity=".18" />
            <stop offset="48%" stopColor="#f72585" stopOpacity=".82" />
            <stop offset="100%" stopColor="#f05a35" stopOpacity=".2" />
          </linearGradient>
          <filter id="neural-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="7" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <g className="neural-loop__links">
          {paths.map((path, index) => <path key={path} d={path} style={{ '--path-delay': `${index * -0.22}s` }} />)}
        </g>

        {!reduceMotion && (
          <g className="neural-loop__signals" aria-hidden="true">
            {paths.map((path, index) => (
              <circle key={path} r={index % 3 === 0 ? 5 : 3.5}>
                <animateMotion dur={`${2.8 + (index % 4) * 0.35}s`} begin={`${index * -0.34}s`} repeatCount="indefinite" path={path} />
              </circle>
            ))}
          </g>
        )}

        <g className="neural-loop__core" aria-hidden="true">
          <circle className="neural-loop__orbit neural-loop__orbit--outer" cx="382" cy="282" r="92" />
          <circle className="neural-loop__orbit neural-loop__orbit--inner" cx="382" cy="282" r="68" />
          <path d="M346 286c18-48 56-48 74-12 12 24-2 54-30 60-31 7-61-20-50-51 8-24 36-39 58-24 20 13 20 43 1 55-19 12-43-2-41-24 2-19 25-29 39-16" />
          <circle cx="382" cy="282" r="36" />
          <circle className="neural-loop__spark neural-loop__spark--one" cx="382" cy="220" r="5" />
          <circle className="neural-loop__spark neural-loop__spark--two" cx="434" cy="305" r="4" />
          <circle className="neural-loop__spark neural-loop__spark--three" cx="340" cy="322" r="4" />
        </g>

        <g className="neural-loop__center-label">
          <text x="382" y="276">INTELIGENCIA</text>
          <text x="382" y="296">CENTRAL</text>
        </g>

        <g className="neural-loop__nodes">
          {nodes.map((node, index) => {
            const labelX = node.side === 'left' ? node.x + 35 : node.x - 35;
            return (
              <g key={node.id} className="neural-loop__node" style={{ '--node-delay': `${index * -0.18}s` }}>
                <circle className="neural-loop__node-ring" cx={node.x} cy={node.y} r="25" />
                <circle className="neural-loop__node-dot" cx={node.x} cy={node.y} r="7" />
                <text x={labelX} y={node.y + 4} textAnchor={node.side === 'left' ? 'start' : 'end'}>{node.label}</text>
              </g>
            );
          })}
        </g>
      </svg>
      <figcaption>
        <span>Sincronización continua</span>
        <strong>Una inteligencia organiza cada señal y la convierte en la siguiente acción.</strong>
      </figcaption>
    </figure>
  );
}

function ProductMockup({ active }) {
  const Icon = active.icon;
  const pipeline = ['Nuevo', 'En atención', 'Agendado'];

  return (
    <MorphContent active={active} className="product-shell-wrap">
      <motion.div
        className="product-glow"
        animate={{ backgroundColor: active.theme.accent }}
        transition={{ duration: 0.7, ease: EASE }}
      />
      <div className="product-shell" aria-label={`Vista interactiva de CavernicolApp para ${active.label}`}>
        <div className="product-shell__bar">
          <div className="window-dots" aria-hidden="true"><i /><i /><i /></div>
          <div className="product-shell__address">app.cavernicolapp.com</div>
          <ShieldCheck aria-label="Conexión segura" />
        </div>
        <div className="product-shell__body">
          <aside className="mock-sidebar" aria-label="Módulos del producto">
            <span className="mock-sidebar__mark"><Icon /></span>
            <span className="is-selected"><LayoutDashboard /></span>
            <span><MessageCircleMore /></span>
            <span><CalendarCheck2 /></span>
            <span><Target /></span>
          </aside>
          <div className="mock-main">
            <header className="mock-header">
              <div>
                <span>HOY EN TU OPERACIÓN</span>
                <strong>{active.label}</strong>
              </div>
              <div className="mock-avatar">SM</div>
            </header>

            <motion.div
              className="smart-alert"
              animate={{ backgroundColor: active.theme.surface, borderColor: active.theme.accent2 }}
              transition={{ duration: 0.65, ease: EASE }}
            >
              <span className="smart-alert__icon"><Zap /></span>
              <div>
                <small>ACTUALIZACIÓN EN TIEMPO REAL</small>
                <strong>{active.alert}</strong>
                <p>{active.resource}</p>
              </div>
              <CircleCheckBig />
            </motion.div>

            <div className="mock-grid">
              <section className="conversation-card">
                <div className="conversation-card__head">
                  <div><MessageCircleMore /><strong>Conversación activa</strong></div>
                  <span>Ahora</span>
                </div>
                <div className="channel-row">
                  <span className="channel-icon"><Instagram /></span>
                  <div><strong>{active.lead}</strong><small>Identidad y contexto unificados</small></div>
                </div>
                <div className="ai-suggestion">
                  <span><Bot /></span>
                  <div><small>SIGUIENTE MEJOR ACCIÓN</small><strong>{active.nextAction}</strong></div>
                  <button type="button" aria-label="Aplicar acción sugerida"><ArrowRight /></button>
                </div>
              </section>

              <section className="agenda-card">
                <div className="agenda-card__head"><CalendarCheck2 /><strong>Agenda</strong></div>
                <div className="agenda-date"><span>18</span><div>JUL<small>Viernes</small></div></div>
                <div className="agenda-slot"><Clock3 /><div><strong>{active.alert}</strong><small>{active.resource}</small></div></div>
              </section>
            </div>

            <section className="pipeline-card">
              <div className="pipeline-card__head"><strong>Flujo comercial</strong><span>Actualizado ahora</span></div>
              <div className="pipeline-track">
                {pipeline.map((stage, index) => (
                  <div key={stage} className="pipeline-stage">
                    <i style={{ backgroundColor: index === 2 ? active.theme.accent : undefined }} />
                    <span>{stage}</span>
                    {index < pipeline.length - 1 && <ChevronRight />}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
      <motion.div className="floating-chip floating-chip--left" layout>
        <Facebook /><span>Lead capturado</span><Check />
      </motion.div>
      <motion.div className="floating-chip floating-chip--right" layout>
        <Sparkles /><span>IA trabajando</span>
      </motion.div>
    </MorphContent>
  );
}

function HonestProof({ active }) {
  return (
    <MorphContent active={active} className="proof-grid">
      <article className="proof-card proof-card--statement">
        <span className="section-kicker">PRUEBA SOCIAL SEGMENTADA</span>
        <h3>Evidencia de {active.proofPrompt}. No testimonios prestados de otro giro.</h3>
        <p>
          Esta landing queda preparada para consumir casos por vertical. Hasta contar con autorización,
          nombre y periodo verificable, no publicamos porcentajes inventados. La confianza también necesita CRM.
        </p>
        <div className="proof-status"><ShieldCheck /><span>Casos verificados en preparación</span></div>
      </article>
      <article className="proof-card proof-card--metrics">
        <span>LO QUE CAVERNICOLAPP TE AYUDA A MEDIR</span>
        {active.proofMetrics.map((metric, index) => (
          <div key={metric} className="proof-metric">
            <strong>0{index + 1}</strong>
            <p>{metric}</p>
            <ArrowRight />
          </div>
        ))}
        <small>Conecta resultados reales cuando exista una línea base aprobada.</small>
      </article>
    </MorphContent>
  );
}

const TOUR_NODES = [
  { key: 'messages', icon: MessageCircleMore, label: 'Conversaciones', className: 'tour-node--one' },
  { key: 'crm', icon: UsersRound, label: 'CRM', className: 'tour-node--two' },
  { key: 'agenda', icon: CalendarCheck2, label: 'Agenda', className: 'tour-node--three' },
  { key: 'automation', icon: Zap, label: 'Automatización', className: 'tour-node--four' },
];

const TOUR_PATHS = [
  'M300 250C220 210 170 130 110 95',
  'M300 250C380 210 430 130 490 95',
  'M300 250C220 300 170 370 110 405',
  'M300 250C380 300 430 370 490 405',
];

const tourContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const tourNodeVariants = {
  hidden: { opacity: 0, scale: 0.7, y: 14 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

function ProductTour() {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className="product-tour"
      variants={tourContainerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
    >
      <motion.div className="tour-node tour-node--center" variants={tourNodeVariants}>
        <Bot /><strong>IA ORQUESTADORA</strong><small>Contexto + reglas + siguiente acción</small>
      </motion.div>
      {TOUR_NODES.map(({ key, icon: NodeIcon, label, className }) => (
        <motion.div key={key} className={`tour-node ${className}`} variants={tourNodeVariants}>
          <NodeIcon /><span>{label}</span>
        </motion.div>
      ))}
      <svg className="tour-lines" viewBox="0 0 600 500" aria-hidden="true">
        {TOUR_PATHS.map((d, index) => (
          <path key={d} d={d} className={reduceMotion ? '' : 'is-flowing'} style={{ '--tour-path-delay': `${index * -0.3}s` }} />
        ))}
        {!reduceMotion && (
          <g className="tour-signals" aria-hidden="true">
            {TOUR_PATHS.map((d, index) => (
              <circle key={d} className="tour-signal" r={index % 2 === 0 ? 4 : 3}>
                <animateMotion dur={`${2.4 + (index % 3) * 0.4}s`} begin={`${index * -0.4}s`} repeatCount="indefinite" path={d} />
              </circle>
            ))}
          </g>
        )}
      </svg>
    </motion.div>
  );
}

const SPHERE_RINGS = [
  { key: 'outline', el: 'circle', cx: 450, cy: 300, r: 190 },
  { key: 'meridian-1', el: 'ellipse', cx: 450, cy: 300, rx: 130, ry: 190 },
  { key: 'meridian-2', el: 'ellipse', cx: 450, cy: 300, rx: 62, ry: 190 },
  { key: 'lat-upper', el: 'ellipse', cx: 450, cy: 232, rx: 150, ry: 44 },
  { key: 'lat-lower', el: 'ellipse', cx: 450, cy: 368, rx: 150, ry: 44 },
  { key: 'equator', el: 'ellipse', cx: 450, cy: 300, rx: 190, ry: 18 },
];
const SPHERE_SIGNAL_PATHS = [
  'M260 300 A190 190 0 0 1 640 300',
  'M300 232 A150 44 0 0 1 600 232',
  'M450 110 A62 190 0 0 1 450 490',
];

function GlobalSignalSphere() {
  const reduceMotion = useReducedMotion();
  return (
    <div className={`global-sphere ${reduceMotion ? 'is-static' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 900 600" className="global-sphere__grid-svg">
        <defs>
          <pattern id="sphere-dots" width="34" height="34" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.4" fill="rgba(255,255,255,.16)" />
          </pattern>
          <radialGradient id="sphere-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--vertical-accent)" stopOpacity=".55" />
            <stop offset="100%" stopColor="var(--vertical-accent)" stopOpacity="0" />
          </radialGradient>
          <filter id="sphere-blur" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="9" />
          </filter>
        </defs>
        <rect width="900" height="600" fill="url(#sphere-dots)" />
        <circle cx="450" cy="300" r="230" fill="url(#sphere-glow)" />
        <g className="global-sphere__wire">
          {SPHERE_RINGS.map((ring, i) =>
            ring.el === 'circle' ? (
              <motion.circle
                key={ring.key} cx={ring.cx} cy={ring.cy} r={ring.r}
                filter={i === 0 ? 'url(#sphere-blur)' : undefined}
                className={i === 0 ? 'global-sphere__ring global-sphere__ring--glow' : 'global-sphere__ring'}
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 1.6, delay: i * 0.18, ease: EASE }}
              />
            ) : (
              <motion.ellipse
                key={ring.key} cx={ring.cx} cy={ring.cy} rx={ring.rx} ry={ring.ry}
                className="global-sphere__ring"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 1.6, delay: i * 0.18, ease: EASE }}
              />
            )
          )}
          {/* crisp bright outline redraw, matching a horizon-glow line over the soft blurred ring above */}
          <motion.circle
            cx="450" cy="300" r="190" className="global-sphere__ring global-sphere__ring--crisp"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.6, ease: EASE }}
          />
        </g>
        {!reduceMotion && (
          <g className="global-sphere__signals">
            {SPHERE_SIGNAL_PATHS.map((d, i) => (
              <circle key={d} className="global-sphere__signal" r={i === 0 ? 4.5 : 3.2}>
                <animateMotion dur={`${4.4 + i * 1.1}s`} begin={`${i * -1.3}s`} repeatCount="indefinite" path={d} />
              </circle>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

const AMBIENT_DRIFT_PATHS = [
  'M-100 120 L 1600 620',
  'M-100 720 L 1600 220',
];

function AmbientNetworkField() {
  const reduceMotion = useReducedMotion();
  return (
    <div className={`ambient-field ${reduceMotion ? 'is-static' : ''}`} aria-hidden="true">
      <div className="ambient-field__orb ambient-field__orb--one" />
      <div className="ambient-field__orb ambient-field__orb--two" />
      <div className="ambient-field__orb ambient-field__orb--three" />
      <svg className="ambient-field__grid-svg" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="ambient-dots" width="46" height="46" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.5" fill="var(--vertical-accent)" />
          </pattern>
        </defs>
        <rect width="1600" height="900" fill="url(#ambient-dots)" />
        {!reduceMotion && (
          <g className="ambient-field__signals">
            {AMBIENT_DRIFT_PATHS.map((d, i) => (
              <circle key={d} className="ambient-field__signal" r={i === 0 ? 3.5 : 2.6}>
                <animateMotion dur={`${26 + i * 9}s`} begin={`${i * -9}s`} repeatCount="indefinite" path={d} />
              </circle>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
}

// Sprint 73 — Landing: formulario de contacto real + burbuja de WhatsApp +
// textos editables desde Super Admin, adaptados por vertical.

function WhatsAppGlyph(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 1.67c2.19 0 4.24.85 5.79 2.4a8.2 8.2 0 0 1 2.4 5.79c0 4.52-3.68 8.2-8.2 8.2a8.2 8.2 0 0 1-4.15-1.13l-.3-.17-3.12.82.83-3.04-.19-.31a8.15 8.15 0 0 1-1.25-4.37c0-4.53 3.68-8.2 8.19-8.2Zm-4.5 4.32c-.15 0-.4.06-.61.3-.21.24-.8.79-.8 1.92 0 1.13.82 2.22.93 2.38.12.15 1.6 2.52 3.96 3.42 1.96.75 2.36.6 2.78.56.42-.04 1.37-.56 1.56-1.1.19-.55.19-1.02.13-1.11-.06-.1-.21-.16-.44-.28-.24-.12-1.37-.68-1.58-.75-.21-.08-.37-.12-.52.12-.15.24-.6.75-.73.9-.14.15-.27.17-.5.06-.24-.12-1-.37-1.9-1.18-.7-.63-1.18-1.4-1.31-1.64-.14-.24-.01-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.52-1.28-.72-1.75-.19-.46-.38-.4-.52-.4Z"/>
    </svg>
  );
}

function mergeVerticalContent(base, override) {
  if (!override) return base;
  const merged = { ...base };
  ['label', 'shortLabel', 'eyebrow', 'problem', 'agitation', 'solution', 'job', 'outcome',
    'alert', 'resource', 'lead', 'nextAction', 'proofPrompt'].forEach((key) => {
    if (override[key]) merged[key] = override[key];
  });
  merged.benefits = base.benefits.map((pair, i) => {
    const n = i + 1;
    return [override[`benefit${n}_title`] || pair[0], override[`benefit${n}_desc`] || pair[1]];
  });
  merged.proofMetrics = base.proofMetrics.map((m, i) => override[`proofMetric${i + 1}`] || m);
  return merged;
}

function getStoredUtm() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = {
    utm_source: params.get('utm_source'), utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'), utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
  };
  const hasAny = Object.values(fromUrl).some(Boolean);
  if (hasAny) {
    try { sessionStorage.setItem('cav_landing_utm', JSON.stringify(fromUrl)); } catch { /* */ }
    return fromUrl;
  }
  try {
    const stored = sessionStorage.getItem('cav_landing_utm');
    if (stored) return JSON.parse(stored);
  } catch { /* */ }
  return fromUrl;
}

// Sprint 73.1 — full-screen entry preloader: black screen + large logo for
// 2s, then a 1s "spiral" exit (rotate + collapse + fade) revealing the
// landing. Unmounts entirely once done so it never blocks scroll/clicks.
function LandingPreloader() {
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState('visible'); // visible -> leaving -> done

  useEffect(() => {
    if (reduceMotion) { setPhase('done'); return undefined; }
    document.documentElement.classList.add('preloader-lock');
    // The exit timer is chained off when 'leaving' actually starts (not a
    // second fixed deadline from mount) — if the main thread is busy right
    // after mount (slow device, heavy first-paint work) and the 2s timer
    // fires late, the 1s exit still gets its full duration instead of being
    // swallowed by an already-overdue second timeout.
    let toDone;
    const toLeaving = window.setTimeout(() => {
      setPhase('leaving');
      toDone = window.setTimeout(() => setPhase('done'), 1000);
    }, 2000);
    return () => {
      window.clearTimeout(toLeaving);
      window.clearTimeout(toDone);
      document.documentElement.classList.remove('preloader-lock');
    };
  }, [reduceMotion]);

  useEffect(() => {
    if (phase === 'done') document.documentElement.classList.remove('preloader-lock');
  }, [phase]);

  if (phase === 'done') return null;

  return (
    <motion.div
      className="landing-preloader"
      initial={{ opacity: 1, rotate: 0, scale: 1 }}
      animate={phase === 'leaving' ? { opacity: 0, rotate: 620, scale: 0 } : { opacity: 1, rotate: 0, scale: 1 }}
      transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
      aria-hidden="true"
      data-testid="landing-preloader"
    >
      <div className="landing-preloader__glow" />
      <motion.div
        className="landing-preloader__mark"
        initial={{ opacity: 0, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        <CavernicolAppLogo inverse />
      </motion.div>
    </motion.div>
  );
}

function WhatsAppBubble({ number, message }) {
  if (!number) return null;
  const href = `https://wa.me/${number.replace(/\D/g, '')}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
  return (
    <a
      className="whatsapp-bubble" href={href} target="_blank" rel="noreferrer"
      aria-label="Escríbenos por WhatsApp" data-testid="landing-whatsapp-bubble"
    >
      <WhatsAppGlyph className="whatsapp-bubble__icon" />
    </a>
  );
}

function LandingContactForm({ publicConfig }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setStatus('error');
      setErrorMsg('Escribe tu nombre y al menos un correo o teléfono.');
      return;
    }
    setStatus('sending');
    const utm = getStoredUtm();
    try {
      await api.post('/public/landing/contact', {
        ...form, turnstile_token: token, page_url: window.location.href, ...utm,
      });
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', message: '', website: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'No se pudo enviar. Intenta de nuevo.');
      turnstileRef.current?.reset();
    }
  };

  if (status === 'sent') {
    return (
      <div className="contact-form contact-form--sent" data-testid="landing-contact-sent">
        <CircleCheckBig />
        <h3>¡Gracias! Ya recibimos tu mensaje.</h3>
        <p>Un asesor se pondrá en contacto contigo en breve.</p>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit} data-testid="landing-contact-form">
      <input
        type="text" name="website" value={form.website} tabIndex={-1} autoComplete="off"
        onChange={(e) => setForm((p) => ({ ...p, website: e.target.value }))}
        className="contact-form__honeypot" aria-hidden="true"
      />
      <div className="contact-form__row">
        <input placeholder="Tu nombre" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
      </div>
      <div className="contact-form__row contact-form__row--split">
        <input type="email" placeholder="Correo" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
        <input type="tel" placeholder="Teléfono" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
      </div>
      <div className="contact-form__row">
        <textarea placeholder="Cuéntanos sobre tu negocio" rows={3} value={form.message} onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))} />
      </div>
      <TurnstileWidget ref={turnstileRef} action="landing_contact" onToken={setToken} />
      {status === 'error' && <p className="contact-form__error">{errorMsg}</p>}
      <button type="submit" className="button-primary" disabled={status === 'sending'}>
        {status === 'sending' ? <Loader2 className="animate-spin" /> : <ArrowRight />}
        {status === 'sending' ? 'Enviando…' : 'Enviar mensaje'}
      </button>
    </form>
  );
}

const pillContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};
const pillItemVariants = {
  hidden: { opacity: 0, y: 8, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: EASE } },
};

export default function LandingPage() {
  const initialVertical = useMemo(() => {
    const param = new URLSearchParams(window.location.search).get('vertical');
    return VERTICALS.some((vertical) => vertical.id === param) ? param : null;
  }, []);
  const [activeId, setActiveId] = useState(initialVertical);
  const [publicConfig, setPublicConfig] = useState(null);
  const [contentOverrides, setContentOverrides] = useState({ shared: {}, verticals: {} });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfgRes, contentRes] = await Promise.allSettled([
          api.get('/public/landing/config'),
          api.get('/public/landing/content'),
        ]);
        if (cancelled) return;
        if (cfgRes.status === 'fulfilled') setPublicConfig(cfgRes.value.data);
        if (contentRes.status === 'fulfilled') setContentOverrides(contentRes.value.data);
      } catch { /* landing keeps working with defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // UTM capture: persist for the session + surface to GTM/GA4's dataLayer so
  // ad-campaign traffic is identifiable once the visitor converts.
  useEffect(() => {
    const utm = getStoredUtm();
    if (Object.values(utm).some(Boolean) && window.dataLayer) {
      window.dataLayer.push({ event: 'cav_utm_capture', ...utm });
    }
  }, []);

  // Marketing pixels — only load on this public page, gated by Super Admin
  // config (Sprint 73), never on the authenticated app.
  useEffect(() => {
    if (!publicConfig) return;
    const { ga4_measurement_id, gtm_container_id, meta_pixel_id, linkedin_partner_id, tiktok_pixel_id } = publicConfig;

    if (gtm_container_id && !document.querySelector('script[data-cav-gtm]')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      const s = document.createElement('script');
      s.async = true; s.dataset.cavGtm = '1';
      s.src = `https://www.googletagmanager.com/gtm.js?id=${gtm_container_id}`;
      document.head.appendChild(s);
    } else if (ga4_measurement_id && !document.querySelector('script[data-cav-ga4]')) {
      const s = document.createElement('script');
      s.async = true; s.dataset.cavGa4 = '1';
      s.src = `https://www.googletagmanager.com/gtag/js?id=${ga4_measurement_id}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', ga4_measurement_id);
    }

    if (meta_pixel_id && !document.querySelector('script[data-cav-meta-pixel]')) {
      /* eslint-disable */
      (function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.dataset.cavMetaPixel='1';t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)})(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq('init', meta_pixel_id);
      window.fbq('track', 'PageView');
    }

    if (linkedin_partner_id && !document.querySelector('script[data-cav-li]')) {
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(linkedin_partner_id);
      const s = document.createElement('script');
      s.async = true; s.dataset.cavLi = '1';
      s.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      document.head.appendChild(s);
    }

    if (tiktok_pixel_id && !document.querySelector('script[data-cav-ttq]')) {
      /* eslint-disable */
      (function (w, d, t) { w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<e.length;n++)ttq.setAndDefer(e,e[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.dataset.cavTtq="1",o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load(t); ttq.page(); }(window, document, tiktok_pixel_id));
      /* eslint-enable */
    }
  }, [publicConfig]);

  const selectedVertical = VERTICALS.find((vertical) => vertical.id === activeId) || null;
  const active = selectedVertical
    ? mergeVerticalContent(selectedVertical, contentOverrides.verticals?.[selectedVertical.id])
    : NEUTRAL_VERTICAL;
  const hasSelection = Boolean(selectedVertical);
  const Icon = active.icon;
  const shared = { ...DEFAULT_SHARED_CONTENT, ...contentOverrides.shared };
  const capabilityPills = (shared.capability_pills_csv || '').split(',').map((s) => s.trim()).filter(Boolean);

  const selectVertical = (id) => {
    setActiveId(id);
    const url = new URL(window.location.href);
    url.searchParams.set('vertical', id);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const contactEmail = publicConfig?.contact_email || 'contacto@cavernicolacreativo.com';
  const demoHref = `mailto:${contactEmail}?subject=${encodeURIComponent(`Demo CavernicolApp para ${active.label}`)}`;
  const mailtoContactHref = `mailto:${contactEmail}`;
  // Sprint 73.2 — cada punto de contacto tiene su propio modo mailto/form,
  // ya no un único interruptor compartido.
  const navDemoHref = (publicConfig?.cta_mode_nav || 'mailto') === 'form' ? '#contacto' : demoHref;
  const heroDemoHref = (publicConfig?.cta_mode_hero || 'mailto') === 'form' ? '#contacto' : demoHref;
  const finalCtaHref = (publicConfig?.cta_mode_final || 'mailto') === 'form' ? '#contacto' : demoHref;
  const footerContactHref = (publicConfig?.cta_mode_footer_contact || 'mailto') === 'form' ? '#contacto' : mailtoContactHref;

  return (
    <motion.div
      className="cav-landing"
      style={{
        '--vertical-accent': active.theme.accent,
        '--vertical-accent-2': active.theme.accent2,
        '--vertical-ink': active.theme.ink,
        '--vertical-surface': active.theme.surface,
        '--vertical-glow': active.theme.glow,
      }}
      animate={{ backgroundColor: active.theme.surface, color: active.theme.ink }}
      transition={{ duration: 0.75, ease: EASE }}
    >
      <title>{hasSelection ? `CavernicolApp para ${active.label} — CRM y automatización con IA` : 'CavernicolApp — CRM y automatización con IA'}</title>
      <meta name="description" content={hasSelection ? `CavernicolApp adapta CRM, agenda, conversaciones e IA a tu operación de ${active.label.toLowerCase()}. Conoce ${active.job}` : 'CavernicolApp conecta CRM, agenda, conversaciones, marketing e IA en una plataforma que se adapta a tu industria.'} />
      <link rel="canonical" href={`https://app.cavernicolapp.com/landing${hasSelection ? `?vertical=${active.id}` : ''}`} />
      <LandingPreloader />
      <AmbientNetworkField />
      <WhatsAppBubble number={publicConfig?.whatsapp_number} message={publicConfig?.whatsapp_message} />
      <header className="landing-nav">
        <a href="#inicio" aria-label="CavernicolApp, ir al inicio"><CavernicolAppLogo /></a>
        <nav aria-label="Navegación principal">
          <a href="#beneficios">Beneficios</a>
          <a href="#producto">Producto</a>
          <a href="#evidencia">Casos</a>
        </nav>
        <div className="landing-nav__actions">
          <Link className="nav-login" to="/login">Iniciar sesión</Link>
          <a className="nav-demo" href={navDemoHref}>Solicitar demo <ArrowRight /></a>
        </div>
      </header>

      <main id="inicio">
        <section className="hero-section" aria-labelledby="hero-title">
          <div className="hero-orb hero-orb--one" />
          <div className="hero-orb hero-orb--two" />
          <div className="hero-copy">
            <div className="hero-badge"><Sparkles /><span>{shared.hero_badge}</span></div>
            <MorphContent active={active} className="hero-story" id="vertical-story">
              <span className="hero-eyebrow"><Icon />{active.eyebrow}</span>
              <h1 id="hero-title">
                {shared.hero_title_static}
                <em> {shared.hero_title_emphasis}</em>
              </h1>
              <p className="hero-problem"><strong>{active.problem}</strong></p>
              <p className="hero-agitation">{active.agitation}</p>
              <p className="hero-solution">{active.solution}</p>
            </MorphContent>
            <div className="hero-actions">
              <a className="button-primary" href={heroDemoHref}>Verlo en mi {active.shortLabel.toLowerCase()} <ArrowRight /></a>
              <a className="button-ghost" href="#producto"><span><Play /></span> Explorar el producto</a>
            </div>
            {/* S99: el camino al diagnóstico. Sin esto la pantalla existiría y
                nadie llegaría — que es lo mismo que no existir. */}
            <Link className="hero-diagnostico" to="/diagnostico">
              <Sparkles />
              <span>
                <strong>¿No sabes cuál te toca?</strong>
                Contesta 12 preguntas y te digo qué necesitas, cuánto cuesta y
                cuánto se te está yendo hoy por no tenerlo.
              </span>
              <ArrowRight />
            </Link>
            <div className="hero-trust">
              <span><Check /> {shared.hero_trust_1}</span>
              <span><Check /> {shared.hero_trust_2}</span>
              <span><Check /> {shared.hero_trust_3}</span>
            </div>
          </div>
          <div className="hero-visual"><CyberNeuronLoop /></div>

          <div className={`switcher-panel ${hasSelection ? 'has-selection' : ''}`} id="industria">
            <div className="switcher-panel__selection">
              <div className="switcher-panel__intro">
                <span><MousePointerClick /></span>
                <div><small>PRIMERO, CUÉNTANOS SOBRE TU OPERACIÓN</small><strong>¿A qué te dedicas?</strong></div>
              </div>
              <p className="switcher-panel__help">Elige una industria para adaptar el mensaje, los beneficios y la demostración a tu forma de trabajar.</p>
              <IndustrySwitcher activeId={activeId} onChange={selectVertical} />
              {!hasSelection && <span className="switcher-panel__pending">Ninguna industria está seleccionada todavía.</span>}
            </div>
            <div className="switcher-panel__preview" aria-live="polite">
              {hasSelection ? (
                <ProductMockup active={active} />
              ) : (
                <div className="industry-preview-empty">
                  <span><MousePointerClick /></span>
                  <div>
                    <small>DEMOSTRACIÓN PERSONALIZADA</small>
                    <strong>Selecciona tu industria</strong>
                    <p>Aquí aparecerá una vista de CavernicolApp con procesos, lenguaje y prioridades relevantes para tu negocio.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="job-strip" aria-label="Trabajo principal a resolver">
          <span className="job-strip__label">EL TRABAJO REAL</span>
          <MorphContent active={active} className="job-strip__content">
            <Target />
            <p>“{active.job}”</p>
            <strong>{active.outcome}</strong>
          </MorphContent>
        </section>

        <section className="benefits-section" id="beneficios">
          <div className="section-heading">
            <span className="section-kicker">{shared.beneficios_kicker}</span>
            <MorphContent active={active}>
              <h2>{shared.beneficios_heading_static}<br /><em>{shared.beneficios_heading_emphasis.replace('{{vertical}}', active.shortLabel.toLowerCase())}</em></h2>
            </MorphContent>
          </div>
          <MorphContent active={active} className="benefits-grid">
            {active.benefits.map(([title, description], index) => {
              const icons = [MessageCircleMore, CalendarCheck2, Zap];
              const BenefitIcon = icons[index];
              return (
                <article key={title} className="benefit-card">
                  <span className="benefit-card__number">0{index + 1}</span>
                  <div className="benefit-card__icon"><BenefitIcon /></div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                  <span className="benefit-card__line" />
                </article>
              );
            })}
          </MorphContent>
        </section>

        <section className="product-section" id="producto">
          <GlobalSignalSphere />
          <div className="product-section__copy">
            <span className="section-kicker">{shared.producto_kicker}</span>
            <h2>{shared.producto_heading_static}<br /><em>{shared.producto_heading_emphasis}</em></h2>
            <p>{shared.producto_copy}</p>
            <motion.div
              className="capability-pills"
              variants={pillContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
            >
              {(capabilityPills.length ? capabilityPills : CAPABILITY_PILLS).map((item) => (
                <motion.span key={item} variants={pillItemVariants}><Check />{item}</motion.span>
              ))}
            </motion.div>
          </div>
          <ProductTour />
        </section>

        <section className="proof-section" id="evidencia">
          <div className="section-heading section-heading--center">
            <span className="section-kicker">{shared.evidencia_kicker}</span>
            <h2>Si eliges {active.label},<br /><em>sólo verás evidencia de {active.proofPrompt}.</em></h2>
          </div>
          <HonestProof active={active} />
        </section>

        <section className="how-section">
          <div className="section-heading">
            <span className="section-kicker">{shared.how_kicker}</span>
            <h2>{shared.how_heading_static} <em>{shared.how_heading_emphasis}</em></h2>
          </div>
          <div className="how-grid">
            {[
              [Building2, shared.how_step1_title, shared.how_step1_desc],
              [MousePointerClick, shared.how_step2_title, shared.how_step2_desc],
              [Zap, shared.how_step3_title, shared.how_step3_desc],
            ].map(([StepIcon, title, copy], index) => (
              <article key={title}>
                <span className="how-index">0{index + 1}</span>
                <StepIcon />
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta__glow" />
          <CavernicolAppLogo inverse compact />
          <MorphContent active={active} className="final-cta__copy">
            <span>PARA {active.label.toUpperCase()}</span>
            <h2>{shared.final_heading_static}<br /><em>{shared.final_heading_emphasis}</em></h2>
            <p>{active.job}</p>
          </MorphContent>
          <div className="final-cta__actions">
            <a className="button-light" href={finalCtaHref}>Solicitar demo personalizada <ArrowRight /></a>
            <Link to="/login">Ya tengo una cuenta</Link>
          </div>
        </section>

        <section className="contact-section" id="contacto">
          <div className="section-heading section-heading--center">
            <span className="section-kicker">HABLEMOS</span>
            <h2>Cuéntanos sobre tu operación.<br /><em>Te contactamos nosotros.</em></h2>
          </div>
          <LandingContactForm publicConfig={publicConfig} />
        </section>
      </main>

      <footer className="landing-footer">
        <CavernicolAppLogo inverse />
        <p>Un producto tecnológico de <a href="https://www.cavernicolacreativo.com/" target="_blank" rel="noreferrer">Cavernícola Creativo</a>.</p>
        <div><a href={footerContactHref}>Contacto</a><Link to="/terminos">Términos</Link><Link to="/privacidad">Privacidad</Link><Link to="/login">Acceso</Link></div>
        <small>© 2026 Dragon Technologies SAPI de CV</small>
      </footer>
    </motion.div>
  );
}
